import { useState } from 'react';
import { Alert, Form, Input, Modal, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { translationsApi } from '@api/translations';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { NamespaceSummary } from '../types';

const { Text } = Typography;

/** Keep namespaces tidy so the public buckets don't fragment (ev-world ≠ EvWorld). */
function normalizeNamespace(raw: string): string {
  return raw.trim().toLowerCase();
}

interface Props {
  open: boolean;
  namespace: NamespaceSummary | null;
  onClose: () => void;
}

export function NamespaceRenameModal({ open, namespace, onClose }: Props) {
  const { t } = useTranslation(['translations', 'common', 'errors']);
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');

  // Clear the field as the modal closes (destroyOnClose unmounts the input, so a
  // reopen mounts fresh from this cleared state) — no reset-in-effect needed.
  const handleClose = () => {
    setNewName('');
    onClose();
  };

  const renameMutation = useMutation({
    mutationFn: (target: string) => {
      if (!namespace) throw new Error('no namespace');
      return translationsApi.renameNamespace(namespace.namespace, target);
    },
    onSuccess: (result) => {
      toast.success(
        t('namespaceManager.toast.renamed', {
          from: result.namespace,
          to: result.newNamespace,
          count: result.affectedRows,
        }),
      );
      void qc.invalidateQueries({ queryKey: ['translations'] });
      handleClose();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const submit = () => {
    const target = normalizeNamespace(newName);
    if (!target) return;
    renameMutation.mutate(target);
  };

  return (
    <Modal
      title={t('renameModal.title')}
      open={open}
      onCancel={handleClose}
      onOk={submit}
      okText={t('renameModal.submit')}
      cancelText={t('common:actions.cancel')}
      okButtonProps={{ loading: renameMutation.isPending, disabled: !normalizeNamespace(newName) }}
      destroyOnClose
    >
      <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('renameModal.warning')} />
      <Form layout="vertical" component="div">
        <Form.Item label={t('renameModal.currentLabel')}>
          <Text code>{namespace?.namespace}</Text>
        </Form.Item>
        <Form.Item label={t('renameModal.newLabel')}>
          <Input
            value={newName}
            placeholder={t('renameModal.placeholder')}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={submit}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
